import * as braze from '@braze/web-sdk';
import { log } from '@intigral/utils';
import { Button, FontIcon, Spinner, Tooltip } from '@intigral/shared';
import { ITEM_CATEGORY } from '@libs/constants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import h from '../../helpers/common';
import { checkIfReminderExists, reminderItemMixpanelProperties, reminderMatchesItem } from '../../helpers/reminder';
import { useReminderItem } from '../../hooks/useReminderItem';
import { reminderCallback } from '../../hooks/useReminderState';
import { setReminderMutationFn, unsetReminderMutationFn, useUserReminders } from '../../store/reducers/reminderSlice';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { isGuestUser } from '../../config/guestMode';
import { FontIconSize } from '@intigral/shared/components/FontIcon';

type IInitialState = typeof import('../../store/defaultState').initialState;

type IconSize = { size: FontIconSize; md: FontIconSize };

const USER_REMINDERS_QUERY_KEY = ['profiles', 'user-reminders'];

interface AddReminderButtonProps {
    item: any;
    index?: number;
    programStartsInMins?: number;
    isSmall?: boolean;
    category?: 'VOD' | 'LINEAR' | 'PPV';
    isComingSoon?: boolean;
    iconOnly?: boolean;
    selectedChannel?: any;
    onClickReminder?: (type: 'SET' | 'UNSET') => void;
    secondary?: boolean;
    shouldCallMixPanelEvent?: boolean;
}

export default ({
    item,
    index,
    programStartsInMins,
    isSmall,
    category,
    isComingSoon,
    iconOnly = false,
    selectedChannel,
    onClickReminder,
    secondary = false,
    shouldCallMixPanelEvent = true,
}: AddReminderButtonProps) => {
    const userInfo = getUserInfo();
    const queryClient = useQueryClient();
    const { data: reminderData } = useUserReminders({ detailed: false }, userInfo);
    const { track }: any = useContext(MixpanelContext);
    const programTitle: string = item?.title;
    const { remindersList, paId, listingId, isReminded, reminderId } = useReminderItem(item, reminderData ?? []);
    const itemCategory = category || (listingId ? ITEM_CATEGORY.LINEAR : ITEM_CATEGORY.VOD);
    const [optimisticAdded, setOptimisticAdded] = useState<boolean | null>(null);

    useEffect(() => {
        setOptimisticAdded(null);
    }, [listingId, paId]);

    const matchesReminder = (reminder: { paId?: number | string; listingId?: number | string }) =>
        reminderMatchesItem(reminder as { paId: number; listingId?: number }, paId, listingId);

    const buildOptimisticReminder = () => ({
        paId: paId ? Number(paId) : paId,
        listingId: listingId ? Number(listingId) : listingId,
        channel: selectedChannel,
    });

    /** remindersList key also holds RemindersCarousel's infinite data */
    const writeReminderCache = (key: unknown[], updater: (old: any[]) => any[]) => {
        queryClient.setQueryData(key, (old: unknown) => {
            if (old === undefined) return updater([]);
            return Array.isArray(old) ? updater(old) : old;
        });
    };

    const applyOptimisticReminderAdd = () => {
        const updater = (old: any[] = []) => {
            if (old.some(matchesReminder)) {
                return old;
            }
            return [...old, buildOptimisticReminder()];
        };

        writeReminderCache(['remindersList', itemCategory], updater);
        writeReminderCache(USER_REMINDERS_QUERY_KEY, updater);
    };

    const applyOptimisticReminderRemove = () => {
        const updater = (old: any[] = []) => old.filter((reminder) => !matchesReminder(reminder));

        writeReminderCache(['remindersList', itemCategory], updater);
        writeReminderCache(USER_REMINDERS_QUERY_KEY, updater);
    };

    const rollbackOptimisticReminder = (context?: {
        previousRemindersList?: unknown;
        previousUserReminders?: unknown;
    }) => {
        if (context?.previousRemindersList !== undefined) {
            queryClient.setQueryData(['remindersList', itemCategory], context.previousRemindersList);
        }
        if (context?.previousUserReminders !== undefined) {
            queryClient.setQueryData(USER_REMINDERS_QUERY_KEY, context.previousUserReminders);
        }
    };

    const snapshotReminderCaches = async () => {
        await queryClient.cancelQueries({ queryKey: ['remindersList', itemCategory] });
        await queryClient.cancelQueries({ queryKey: USER_REMINDERS_QUERY_KEY });

        return {
            previousRemindersList: queryClient.getQueryData(['remindersList', itemCategory]),
            previousUserReminders: queryClient.getQueryData(USER_REMINDERS_QUERY_KEY),
        };
    };

    const setReminderMutation = useMutation({
        mutationFn: setReminderMutationFn,
        onMutate: async () => {
            const previousCaches = await snapshotReminderCaches();
            applyOptimisticReminderAdd();
            return previousCaches;
        },
        onSuccess: ({ data }) => {
            const channels = data.notifChannels;
            queryClient.invalidateQueries({ queryKey: USER_REMINDERS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['remindersList', itemCategory] });
            braze?.getUser()?.addToCustomAttributeArray('reminder_programs_listing_ids', listingId);
            braze?.getUser()?.setLanguage(h.getLang());
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, item, selectedChannel);
            if (shouldCallMixPanelEvent)
                reminderCallback(mixpanelProperties, 'set', null, item, track, isComingSoon, data.notifChannels);
        },
        onError: (error, _vars, context) => {
            rollbackOptimisticReminder(context);
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, item, selectedChannel);
            if (shouldCallMixPanelEvent)
                reminderCallback(mixpanelProperties, 'errorSet', error, item, track, isComingSoon, []);
        },
        onSettled: () => {
            setOptimisticAdded(null);
        },
    });

    const unsetReminderMutation = useMutation({
        mutationFn: unsetReminderMutationFn,
        onMutate: async () => {
            const previousCaches = await snapshotReminderCaches();
            applyOptimisticReminderRemove();
            return previousCaches;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: USER_REMINDERS_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: ['remindersList', itemCategory] });
            braze?.getUser()?.removeFromCustomAttributeArray('reminder_programs_listing_ids', listingId);
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, item, selectedChannel);
            if (shouldCallMixPanelEvent)
                reminderCallback(mixpanelProperties, 'unset', null, item, track, isComingSoon, []);
        },
        onError: (error, _vars, context) => {
            rollbackOptimisticReminder(context);
            const mixpanelProperties = reminderItemMixpanelProperties(itemCategory, item, selectedChannel);
            if (shouldCallMixPanelEvent)
                reminderCallback(mixpanelProperties, 'errorUnset', error, item, track, isComingSoon, []);
        },
        onSettled: () => {
            setOptimisticAdded(null);
        },
    });

    if (isGuestUser(userInfo)) {
        return null;
    }

    const isCutOffTime = programStartsInMins <= h.reminderCutoffTime();
    const iconSizes: IconSize = { size: isSmall ? 'x-small' : 'smaller', md: isSmall ? 'x-small' : 'medium' };

    const isReminderAdded = optimisticAdded ?? checkIfReminderExists(remindersList, paId, listingId);

    const handleClickReminder = () => {
        const nextValue = !isReminderAdded;
        setOptimisticAdded(nextValue);

        if (isReminderAdded) {
            if (onClickReminder) {
                onClickReminder('UNSET');
            }
            unsetReminderMutation.mutate({
                reminderId,
                listingId,
                programTitle,
                index,
                user: userInfo,
            });
        } else {
            if (onClickReminder) {
                onClickReminder('SET');
            }
            setReminderMutation.mutate({
                paId,
                listingId,
                programTitle,
                index,
                type: itemCategory,
                user: userInfo,
                language: h.getLang(),
            });
        }
    };
    const icon = isCutOffTime ? (
        <FontIcon name="bell-off" {...iconSizes} />
    ) : (
        <FontIcon name={isSmall ? 'bell-filled' : 'bell'} {...iconSizes} />
    );

    if (isComingSoon)
        if (iconOnly) {
            const label = __(isReminderAdded ? 'btn_unset_reminder' : 'btn_set_reminder');
            return (
                <Button
                    social
                    compact
                    primary={!secondary}
                    disabled={category === 'PPV' ? true : false}
                    onClick={handleClickReminder}
                    aria-label={label}
                    title={label}
                    data-test-id="popup-reminder"
                >
                    {/* Figma "Reminder & Favorite Logic" */}
                    {isReminderAdded ? (
                        <FontIcon name="bell-filled" color="orange" size="smaller" md="smaller" />
                    ) : (
                        <FontIcon name="bell" size="smaller" md="smaller" />
                    )}
                </Button>
            );
        }

    return (
        <Button
            // loading={unsetReminderMutation.isPending}
            compact
            disabled={false}
            onClick={handleClickReminder}
            primary={!secondary}
        >
            {isReminderAdded ? (
                <>
                    <span>{__('btn_unset_reminder')}</span>
                    <FontIcon name="bell-filled" color="orange" size="small" />
                </>
            ) : (
                <>
                    <span> {__('btn_set_reminder')}</span>
                    <FontIcon name="bell" size="small" />
                </>
            )}
        </Button>
    );

    return (
        <span
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!isCutOffTime) {
                    if (isReminded) {
                        unsetReminderMutation.mutate({
                            reminderId,
                            listingId,
                            programTitle,
                            index,
                            user: userInfo,
                        });
                    } else {
                        setReminderMutation.mutate({
                            paId,
                            listingId,
                            programTitle,
                            index,
                            type: itemCategory,
                            user: userInfo,
                            language: h.getLang(),
                        });
                    }
                    // isReminded ? unSetReminderHandler() : setReminderHandler();
                }
            }}
            style={{ display: 'flex' }}
            data-test-id={isCutOffTime ? 'reminder_disabled' : `reminder_${isReminded ? 'actived' : 'non_active'}`}
        >
            {setReminderMutation.isPending || unsetReminderMutation.isPending ? (
                <span>
                    <Spinner inline />
                </span>
            ) : (
                <Tooltip
                    icon={icon}
                    text={
                        isCutOffTime
                            ? `${
                                  programStartsInMins > 1
                                      ? __('tooltip_starting_times', { programStartsInMins: programStartsInMins })
                                      : __('tooltip_starting_time', { programStartsInMins: programStartsInMins })
                              }`
                            : isReminded
                            ? __('tooltip_reminder_set')
                            : __('tooltip_remind_me')
                    }
                />
            )}
        </span>
    );
};
